// =============================================================================
// ESTILOS GLOBALES - SISTEMA DE DISEÑO TADMIN
// =============================================================================
// Estilos globales que utilizan los tokens del sistema de diseño
// para crear una base consistente en toda la aplicación

import { createGlobalStyle } from 'styled-components';
import { colors, typography, spacing, shadows, borders } from './tokens/index.js';

export const GlobalStyles = createGlobalStyle`
  /* =============================================================================
     RESET Y NORMALIZACIÓN
     ============================================================================= */
  
  /* Reset básico */
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  /* HTML y Body */
  html {
    font-size: 16px; /* Base para rem */
    line-height: 1.15;
    -webkit-text-size-adjust: 100%;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  body {
    font-family: ${typography.fontFamilies.primary};
    font-size: ${typography.fontSizes.base};
    font-weight: ${typography.fontWeights.normal};
    line-height: ${typography.lineHeights.normal};
    color: ${props => props.theme?.colors?.text?.primary || colors.light.text.primary};
    background-color: ${props => props.theme?.colors?.background?.default || colors.light.background.default};
    min-height: 100vh;
    overflow-x: hidden;
  }
  
  /* =============================================================================
     TIPOGRAFÍA GLOBAL
     ============================================================================= */
  
  /* Headings */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${typography.fontFamilies.primary};
    font-weight: ${typography.fontWeights.semiBold};
    line-height: ${typography.lineHeights.tight};
    color: ${props => props.theme?.colors?.text?.primary || colors.light.text.primary};
    margin-bottom: ${spacing.spacing[4]};
  }
  
  h1 {
    font-size: ${typography.textStyles.h1.fontSize};
    font-weight: ${typography.textStyles.h1.fontWeight};
    line-height: ${typography.textStyles.h1.lineHeight};
    letter-spacing: ${typography.textStyles.h1.letterSpacing};
  }
  
  h2 {
    font-size: ${typography.textStyles.h2.fontSize};
    font-weight: ${typography.textStyles.h2.fontWeight};
    line-height: ${typography.textStyles.h2.lineHeight};
    letter-spacing: ${typography.textStyles.h2.letterSpacing};
  }
  
  h3 {
    font-size: ${typography.textStyles.h3.fontSize};
    font-weight: ${typography.textStyles.h3.fontWeight};
    line-height: ${typography.textStyles.h3.lineHeight};
    letter-spacing: ${typography.textStyles.h3.letterSpacing};
  }
  
  h4 {
    font-size: ${typography.textStyles.h4.fontSize};
    font-weight: ${typography.textStyles.h4.fontWeight};
    line-height: ${typography.textStyles.h4.lineHeight};
  }
  
  h5 {
    font-size: ${typography.textStyles.h5.fontSize};
    font-weight: ${typography.textStyles.h5.fontWeight};
    line-height: ${typography.textStyles.h5.lineHeight};
  }
  
  h6 {
    font-size: ${typography.textStyles.h6.fontSize};
    font-weight: ${typography.textStyles.h6.fontWeight};
    line-height: ${typography.textStyles.h6.lineHeight};
  }
  
  /* Párrafos y texto */
  p {
    font-size: ${typography.textStyles.body1.fontSize};
    line-height: ${typography.textStyles.body1.lineHeight};
    margin-bottom: ${spacing.spacing[4]};
    color: ${props => props.theme?.colors?.text?.secondary || colors.light.text.secondary};
  }
  
  /* Enlaces */
  a {
    color: ${props => props.theme?.colors?.primary?.main || colors.light.primary.main};
    text-decoration: none;
    transition: color 0.2s ease-in-out;
    
    &:hover {
      color: ${props => props.theme?.colors?.primary?.dark || colors.light.primary.dark};
      text-decoration: underline;
    }
    
    &:focus {
      outline: 2px solid ${props => props.theme?.colors?.primary?.main || colors.light.primary.main};
      outline-offset: 2px;
      border-radius: ${borders.radius.sm};
    }
  }
  
  /* Listas */
  ul, ol {
    margin-bottom: ${spacing.spacing[4]};
    padding-left: ${spacing.spacing[6]};
  }
  
  li {
    margin-bottom: ${spacing.spacing[2]};
  }
  
  /* Código */
  code {
    font-family: ${typography.fontFamilies.mono};
    font-size: ${typography.textStyles.code.fontSize};
    background-color: ${props => props.theme?.colors?.background?.level1 || colors.light.background.level1};
    color: ${props => props.theme?.colors?.text?.primary || colors.light.text.primary};
    padding: ${spacing.spacing[1]} ${spacing.spacing[2]};
    border-radius: ${borders.radius.sm};
    border: 1px solid ${props => props.theme?.colors?.border?.light || colors.light.border.light};
  }
  
  pre {
    font-family: ${typography.fontFamilies.mono};
    font-size: ${typography.textStyles.codeBlock.fontSize};
    background-color: ${props => props.theme?.colors?.background?.level2 || colors.light.background.level2};
    color: ${props => props.theme?.colors?.text?.primary || colors.light.text.primary};
    padding: ${spacing.spacing[6]};
    border-radius: ${borders.radius.md};
    overflow-x: auto;
    margin-bottom: ${spacing.spacing[6]};
    
    code {
      background: none;
      border: none;
      padding: 0;
    }
  }
  
  /* =============================================================================
     ELEMENTOS DE FORMULARIO
     ============================================================================= */
  
  /* Formularios */
  input, textarea, select {
    font-family: ${typography.fontFamilies.primary};
    font-size: ${typography.textStyles.body1.fontSize};
    color: ${props => props.theme?.colors?.text?.primary || colors.light.text.primary};
    background-color: ${props => props.theme?.colors?.background?.default || colors.light.background.default};
    border: 1px solid ${props => props.theme?.colors?.border?.light || colors.light.border.light};
    border-radius: ${borders.radius.md};
    padding: ${spacing.spacing[3]} ${spacing.spacing[4]};
    transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme?.colors?.primary?.main || colors.light.primary.main};
      box-shadow: 0 0 0 3px ${props => props.theme?.colors?.primary?.light || colors.light.primary.light}33;
    }
    
    &:disabled {
      background-color: ${props => props.theme?.colors?.background?.level1 || colors.light.background.level1};
      color: ${props => props.theme?.colors?.text?.disabled || colors.light.text.disabled};
      cursor: not-allowed;
    }
  }
  
  /* Botones */
  button {
    font-family: ${typography.fontFamilies.primary};
    font-size: ${typography.textStyles.button.fontSize};
    font-weight: ${typography.textStyles.button.fontWeight};
    line-height: ${typography.textStyles.button.lineHeight};
    border: none;
    border-radius: ${borders.radius.md};
    padding: ${spacing.spacing[3]} ${spacing.spacing[6]};
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    
    &:focus {
      outline: 2px solid ${props => props.theme?.colors?.primary?.main || colors.light.primary.main};
      outline-offset: 2px;
    }
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
  
  /* =============================================================================
     UTILIDADES GLOBALES
     ============================================================================= */
  
  /* Clases de utilidad para accesibilidad */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  
  /* Clases de utilidad para focus */
  .focus-visible {
    outline: 2px solid ${props => props.theme?.colors?.primary?.main || colors.light.primary.main};
    outline-offset: 2px;
  }
  
  /* Clases de utilidad para texto */
  .text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .text-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  /* Clases de utilidad para espaciado */
  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 ${spacing.spacing[4]};
    
    @media (min-width: 768px) {
      padding: 0 ${spacing.spacing[6]};
    }
    
    @media (min-width: 1024px) {
      padding: 0 ${spacing.spacing[8]};
    }
  }
  
  /* =============================================================================
     SCROLLBARS PERSONALIZADOS
     ============================================================================= */
  
  /* Webkit scrollbars */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: ${props => props.theme?.colors?.background?.level1 || colors.light.background.level1};
    border-radius: ${borders.radius.full};
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme?.colors?.border?.medium || colors.light.border.medium};
    border-radius: ${borders.radius.full};
    
    &:hover {
      background: ${props => props.theme?.colors?.border?.dark || colors.light.border.dark};
    }
  }
  
  /* Firefox scrollbars */
  * {
    scrollbar-width: thin;
    scrollbar-color: ${props => props.theme?.colors?.border?.medium || colors.light.border.medium} 
                     ${props => props.theme?.colors?.background?.level1 || colors.light.background.level1};
  }
  
  /* =============================================================================
     ANIMACIONES GLOBALES
     ============================================================================= */
  
  /* Transiciones suaves para elementos interactivos */
  button, a, input, textarea, select {
    transition: all 0.2s ease-in-out;
  }
  
  /* Animación de entrada suave */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }
  
  /* Animación de carga */
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  
  .spin {
    animation: spin 1s linear infinite;
  }
  
  /* =============================================================================
     RESPONSIVE DESIGN
     ============================================================================= */
  
  /* Ajustes para dispositivos móviles */
  @media (max-width: 767px) {
    body {
      font-size: ${typography.textStyles.body1.fontSize};
    }
    
    h1 {
      font-size: ${typography.textStyles.h2.fontSize};
    }
    
    h2 {
      font-size: ${typography.textStyles.h3.fontSize};
    }
    
    h3 {
      font-size: ${typography.textStyles.h4.fontSize};
    }
    
    .container {
      padding: 0 ${spacing.spacing[3]};
    }
  }
  
  /* Ajustes para tablets */
  @media (min-width: 768px) and (max-width: 1023px) {
    .container {
      padding: 0 ${spacing.spacing[6]};
    }
  }
  
  /* Ajustes para desktop */
  @media (min-width: 1024px) {
    .container {
      padding: 0 ${spacing.spacing[8]};
    }
  }
  
  /* =============================================================================
     TEMA OSCURO
     ============================================================================= */
  
  /* Variables CSS para tema oscuro */
  [data-theme="dark"] {
    color-scheme: dark;
    
    body {
      background-color: ${colors.dark.background.default};
      color: ${colors.dark.text.primary};
    }
    
    h1, h2, h3, h4, h5, h6 {
      color: ${colors.dark.text.primary};
    }
    
    p {
      color: ${colors.dark.text.secondary};
    }
    
    a {
      color: ${colors.dark.primary.main};
      
      &:hover {
        color: ${colors.dark.primary.light};
      }
    }
    
    input, textarea, select {
      background-color: ${colors.dark.background.level1};
      border-color: ${colors.dark.border.light};
      color: ${colors.dark.text.primary};
      
      &:focus {
        border-color: ${colors.dark.primary.main};
        box-shadow: 0 0 0 3px ${colors.dark.primary.light}33;
      }
    }
    
    code {
      background-color: ${colors.dark.background.level2};
      color: ${colors.dark.text.primary};
      border-color: ${colors.dark.border.light};
    }
    
    pre {
      background-color: ${colors.dark.background.level2};
      color: ${colors.dark.text.primary};
    }
    
    ::-webkit-scrollbar-track {
      background: ${colors.dark.surface.default};
    }
    
    ::-webkit-scrollbar-thumb {
      background: ${colors.dark.border.default};
      
      &:hover {
        background: ${colors.dark.border.light};
      }
    }
  }
  
  /* =============================================================================
     PRINT STYLES
     ============================================================================= */
  
  @media print {
    * {
      background: transparent !important;
      color: black !important;
      box-shadow: none !important;
      text-shadow: none !important;
    }
    
    a, a:visited {
      text-decoration: underline;
    }
    
    a[href]:after {
      content: " (" attr(href) ")";
    }
    
    abbr[title]:after {
      content: " (" attr(title) ")";
    }
    
    .no-print {
      display: none !important;
    }
  }
`;

export default GlobalStyles;